/* eslint-disable max-lines */
import { zip } from "lodash";
import { isValidUSPhoneNumber } from "utils/phone";
import eachFollows, { ValidationResult } from "./each-follows";
import { fields } from "./fields";
import { mappedBy, selectValues } from "./query";
import { Payload, Record } from "./types";
import { parseDateTime } from "./transform";

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

type ProblemShared = {
  type: "default" | "global" | "record";
  key: string;
}
type ProblemDefault = ProblemShared & {
  type: "default";
  data: {
    field: string;
    value: string;
    default: string;
    index?: number;
  };
}

type ProblemRecord = ProblemShared & {
  type: "record";
  data: {
    field: string;
    value: string;
    index?: number;
  };
}

type ProblemGlobal = ProblemShared & {
  type: "global";
  data: {
    message: string;
    index?: number;
  };
}

// Problem represents a single validation issue including scope, severity, and
// the issue message.
export type Problem = ProblemDefault | ProblemGlobal | ProblemRecord;

// ProblemSearcher represents a function which receives a Payload<Record> and
// returns none or more problems with the given payload.
type ProblemSearcher = (payload: Payload<Record>) => Problem[];
export type DynamicProblemSearcher = (
  validators: ProblemSearcher[],
  payload: Payload<Record>,
) => Problem[];

// Common email domain typos
export const domainCorrections = {
  "gmial.com": "gmail.com",
  "gamil.com": "gmail.com",
  "gmal.com": "gmail.com",
  "gmail.co": "gmail.com",
  "gmail.cm": "gmail.com",
  "hotmial.com": "hotmail.com",
  "hotmal.com": "hotmail.com",
  "hotmail.co": "hotmail.com",
  "hotmail.cm": "hotmail.com",
  "yaho.com": "yahoo.com",
  "yahooo.com": "yahoo.com",
  "yahoo.co": "yahoo.com",
  "yahoo.cm": "yahoo.com",
  "ymal.com": "ymail.com",
  "ymail.co": "ymail.com",
  "ymail.cm": "ymail.com",
};

export const verifyEmails: ProblemSearcher = payload => {
  const emailFields = [
    "household_member_0_email",
    "contact_email",
  ];

  const problems: Problem[] = [];

  for (const field of emailFields) {
    const emails = selectValues(payload, field, true);

    emails.forEach((email, index) => {
      if (!email) return;
      if (isValidEmail(email)) {
        // Check for misspelled domains
        const [, domain] = email.split("@");
        if (domain && domain.toLowerCase() in domainCorrections) {
          problems.push({
            type: "record",
            key: `typo-${field}`,
            data: {
              field,
              value: email,
              index,
            },
          });
        }
      } else {
        problems.push({
          type: "record",
          key: `invalid-${field}`,
          data: {
            field,
            value: email,
            index,
          },
        });
      }
    });
  }

  return problems;
};

export const verifyPhoneNumbers: ProblemSearcher = payload => {
  const phoneFields = [
    "household_member_0_primary_phone",
    "household_member_0_secondary_phone",
    "contact_primary_phone",
  ];

  const problems: Problem[] = [];

  for (const field of phoneFields) {
    const phoneNumbers = selectValues(payload, field, true);

    phoneNumbers.forEach((phoneNumber, index) => {
      if (phoneNumber && !isValidUSPhoneNumber(phoneNumber)) {
        problems.push({
          type: "record",
          key: `invalid-${field}`,
          data: {
            field,
            value: phoneNumber,
            index,
          },
        });
      }
    });
  }

  return problems;
};

export const verifyContactInformation: ProblemSearcher = payload => {
  const problems: Problem[] = [];
  const emailFields = ["household_member_0_email", "contact_email"];
  const phoneFields = [
    "household_member_0_primary_phone",
    "household_member_0_secondary_phone",
    "contact_primary_phone",
  ];

  const mappedEmailFields = emailFields.map(field =>
    mappedBy(payload, field)).filter(Boolean) as string[];
  const mappedPhoneFields = phoneFields.map(field =>
    mappedBy(payload, field)).filter(Boolean) as string[];

  if (mappedEmailFields.length === 0 && mappedPhoneFields.length === 0) {
    return problems; // No contact fields mapped, no need to check
  }

  payload.data.forEach((record, index) => {
    const hasEmail = mappedEmailFields.some(field => {
      const value = record[field];
      return value && value.trim() !== "";
    });

    const hasPhone = mappedPhoneFields.some(field => {
      const value = record[field];
      return value && value.trim() !== "";
    });

    // Only check for missing contact info if we have mapped fields of that type
    const needsEmail = mappedEmailFields.length > 0;
    const needsPhone = mappedPhoneFields.length > 0;

    // Report missing contact info only if all mapped fields are empty
    if ((needsEmail && !hasEmail) && (needsPhone && !hasPhone)) {
      problems.push({
        type: "default",
        key: "missing-contact-info",
        data: {
          field: "household_member_0_email",
          value: "No email or phone provided",
          default: "Please provide at least one contact method",
          index,
        },
      });
    }
  });

  return problems;
};

// verifyRequiredFields returns a [Problem] if the user has not mapped all the
// necessary fields for a useful export, or an empty array otherwise.
export const verifyRequiredFields: ProblemSearcher = payload => {
  if (!mappedBy(payload, "date") && !mappedBy(payload, "position_number")) {
    return [
      {
        type: "global",
        key: "required-fields",
        data: { message: "Either 'Submit date' or 'Position' must be mapped to." },
      },
    ];
  }

  return [];
};

// verifyDateOrder returns a [Problem] if the values mapped to the "date"
// column are not in consistent ascending order (oldest first), otherwise [].
export const verifyDateOrder: ProblemSearcher = payload => {

  // Verify that all 'Submit date' values are in ascending order (oldest first).
  // While we're at it, if we encounter an invalid date format, return early.
  const dates = selectValues(payload, "date", true);
  const times = selectValues(payload, "time", true);
  const { pass, reason, index } =
    eachFollows(zip(dates, times), (prev, next, passedIndex): boolean | ValidationResult => {
      const { result: prevDate } = parseDateTime(prev[0], prev[1]);
      const { valid, result: nextDate } = parseDateTime(next[0], next[1]);
      if (!valid) {
        return {
          pass: false,
          reason: next.filter(Boolean).join(" "),
          index: passedIndex,
        };
      }
      const prevtime = new Date(prevDate).getTime();
      const nexttime = new Date(nextDate).getTime();
      if (prevtime > nexttime) {
        return {
          pass: false,
          reason: next.filter(Boolean).join(" "),
          index: passedIndex,
        };
      }

      return true;
    });

  if (!pass) {
    return [{ type: "record", key: "submit-date-order", data: { field: "date", value: reason, index } }];
  }

  return [];
};

const isInvalidDate = ([date, time]: (string | undefined)[]) => {
  const { valid } = parseDateTime(date, time);
  return valid === false;
};

const isEmptyDateTime = ([date, time]: (string | undefined)[]) => {
  const result = { isEmptyDate: false, isEmptyTime: false };
  if (!date) result.isEmptyDate = true;
  if (!time) result.isEmptyTime = true;
  return result;
};

// verifyDateFormat returns a [Problem] if the date + time do not create a
// valid DateTime string, otherwise [].
export const verifyDateFormat: ProblemSearcher = payload => {
  const dates = selectValues(payload, "date", true);
  const times = selectValues(payload, "time", true);

  const combinedDates = zip(dates, times);

  const problems = combinedDates.map((date, index) => {
    const { isEmptyDate, isEmptyTime } = isEmptyDateTime(date);
    if (isEmptyDate || isEmptyTime) {
      if (isEmptyDate) {
        return {
          type: "default",
          key: "missing-date-time",
          data: {
            field: "date",
            value: date.filter(Boolean).join(", "),
            message: "Missing date will be set to today",
            index,
          },
        };
      }
      return {
        type: "default",
        key: "missing-time",
        data: {
          field: "time",
          value: "-",
          message: "Missing time will be set to 12AM GMT",
          index,
        },
      };

    } else if (isInvalidDate(date)) {
      return {
        type: "record",
        key: "date-format",
        data: {
          field: "date",
          value: date.filter(Boolean).join(", "),
          index,
        },
      };
    }
    return null;

  }).filter(problem => problem !== null) as ProblemRecord[];

  return problems;
};


const getAllOutOfOrder = (input: string[]) => input.reduce(
  (acc, curr, index) => {
    if (index > 0) {
      if (Number(curr) === Number(input[index - 1])) {
        acc.push({ index, value: curr, type: "duplicate" });
      } else if (Number(curr) < Number(input[index - 1])) {
        acc.push({ index, value: curr, type: "out-of-order" });
      }
    }
    return acc;
  }
  , [] as {index: number; value: string; type: string}[],
);

// verifyPositionOrder returns a [Problem] if the values mapped to the
// "position_number" column are not in consistent ascending order.
export const verifyPositionOrder: ProblemSearcher = payload => {
  const positions = selectValues(payload, "position_number", true);
  const errors = getAllOutOfOrder(positions);
  if (errors.length === 0) return [];
  // If there are errors, we need return all the problems
  const problems = errors.map(({ index, value, type }) => {
    if (type === "duplicate") {
      return ({
        type: "default" as const,
        key: "duplicate-position",
        data: {
          field: "position_number",
          value,
          index,
          default: "Duplicate position number",
        },
      });
    }

    // If the type is "out-of-order", we need to return a record problem
    return ({
      type: "record" as const,
      key: "position-order",
      data: {
        field: "position_number",
        value,
        index,
      },
    });
  });

  return problems;
};


const getSchema = (payload: Payload<Record>, name: string) => {
  const emptyMapped = { field: "" };
  if (!(name in payload.mapping)) {
    return { mapped: emptyMapped };
  }

  const mapped = payload.mapping[name] || emptyMapped;
  if (!(mapped?.field in fields)) {
    return { mapped: emptyMapped };
  }

  const field = fields[mapped.field];
  const { schema } = field;
  return {
    schema,
    mapped: (typeof schema === "undefined") ? emptyMapped : mapped,
  };
};

// verifySchemas returns a [Problem] for each value that fails its schema
// validation check, otherwise [].
export const verifySchemas: ProblemSearcher = payload => payload.data.reduce(
  (problems, record, index) =>
    problems.concat(
      Object.entries(record).reduce(
        (problemsNested, [name, value]) => {
          const { schema, mapped } = getSchema(payload, name);
          if (!schema) return problemsNested;
          // Transform and validate the given value using the schema
          // configured for this field. Should validation fail, we know the
          // value is truly broken somehow.
          if (!schema.isValidSync(value)) {
            return problemsNested.concat([
              {
                type: "record",
                key: `schema/${mapped.field}`,
                data: {
                  field: mapped.field,
                  index,
                  value,
                },
              },
            ]);
          }

          // Otherwise, we'll want to warn users when we will be
          // automatically picking a default value for this field. Re-run
          // the validation check but using { strict: true } to avoid
          // any transformations configured for the schema and return a
          // Problem should it fail.
          if (typeof schema.getDefault() === "undefined") return problemsNested;
          if (schema.required()
            .isValidSync(value, { strict: true })) return problemsNested;
          return problemsNested.concat([
            {
              type: "default",
              key: `default/${mapped.field}`,
              data: {
                field: mapped.field,
                value,
                default: `${schema.getDefault()}`,
                index,
              },
            },
          ]);
        },
        [] as Problem[],
      ),
    ),
  [] as Problem[],
);

// verifyDeniedAndMovedIn returns a [Problem] if any row has both denied and moved_in
// set to true, otherwise [].
export const verifyDeniedAndMovedIn: ProblemSearcher = payload => {
  const problems: Problem[] = [];

  // Check if both fields are mapped
  if (!mappedBy(payload, "denied") || !mappedBy(payload, "moved_in")) {
    return [];
  }

  const deniedValues = selectValues(payload, "denied", true);
  const movedInValues = selectValues(payload, "moved_in", true);

  // Define truthy values that should be considered as "true"
  const truthyValues = ["y", "x", "yes", "true"];

  deniedValues.forEach((value, index) => {
    const deniedValue = value?.toLowerCase();
    const movedInValue = movedInValues[index]?.toLowerCase();

    const isDenied = deniedValue && truthyValues.includes(deniedValue);
    const isMovedIn = movedInValue && truthyValues.includes(movedInValue);

    if (isDenied && isMovedIn) {
      problems.push({
        type: "record",
        key: "invalid-status-combination",
        data: {
          field: "denied",
          value: value || "",
          index,
        },
      });
    }
  });

  return problems;
};
